// src/pages/agent/Inbox.js
// Email inbox tied to the homeiqa.com mailboxes. Shows four views:
//   - Shared:    contracts@/hello@/etc — all active agents see
//   - My inbox:  laketia@/denicia@ — only the recipient agent sees
//   - Sent:      messages this agent has sent
//   - Archived:  soft-deleted messages — kept in Firestore, hidden
//                from the active tabs. Can be restored.
//
// Font sizes are intentionally large for at-a-glance scanning.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { useIsMobile } from '../../lib/useIsMobile';
import { PageHeader, Card, Btn, Field, Input, TextArea, Pill, BORDER, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT } from '../../components/agent/UI';

export default function Inbox() {
  const { profile } = useAgentAuth();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('shared'); // 'shared' | 'mine' | 'sent' | 'archived'
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // selected message
  const [showCompose, setShowCompose] = useState(false);
  const [reply, setReply] = useState(null);
  const [pendingArchive, setPendingArchive] = useState(null); // { msg, action: 'archive'|'restore' }
  const [refreshing, setRefreshing] = useState(false);
  // Increment to force-rebuild the snapshot subscription (manual refresh).
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const manualRefresh = async () => {
    setRefreshing(true);
    setRefreshKey(k => k + 1); // re-runs the subscription effect
    setTimeout(() => {
      setRefreshing(false);
      setLastRefreshAt(new Date());
    }, 600);
  };

  // Subscribe to the right slice of mailbox based on the active tab.
  useEffect(() => {
    if (!profile?.uid) return;
    setLoading(true);
    let q;
    if (tab === 'shared') {
      q = query(collection(db, 'mailbox'), where('scope', '==', 'shared'), orderBy('sentAt', 'desc'));
    } else if (tab === 'mine') {
      q = query(collection(db, 'mailbox'), where('toUid', '==', profile.uid), orderBy('sentAt', 'desc'));
    } else if (tab === 'sent') {
      // Owner sees every outbound message (whoever sent it). Contract
      // agents see only their own. Lead submitters never get here.
      if (profile.role === 'admin') {
        q = query(collection(db, 'mailbox'), where('direction', '==', 'out'), orderBy('sentAt', 'desc'));
      } else {
        q = query(collection(db, 'mailbox'), where('fromUid', '==', profile.uid), orderBy('sentAt', 'desc'));
      }
    } else {
      // Archived — pull from all three buckets, filter client-side.
      q = query(collection(db, 'mailbox'), where('scope', 'in', ['shared', 'personal']), orderBy('sentAt', 'desc'));
    }
    // Archived messages older than this drop out of the UI entirely (kept
     // in Firestore for retention/audit — just not surfaced in the portal).
    const ARCHIVE_HIDE_MS = 30 * 86400000; // 30 days
    const cutoffMs = Date.now() - ARCHIVE_HIDE_MS;
    const notExpired = (m) => {
      if (!m.archived) return true;
      const archivedMs = m.archivedAt?.toMillis?.() ?? m.sentAt?.toMillis?.() ?? 0;
      return archivedMs === 0 || archivedMs > cutoffMs;
    };

    const unsub = onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Active tabs hide archived; Archived tab shows ONLY archived. In
      // both cases we drop archived items older than 30 days.
      const filtered = tab === 'archived'
        ? all.filter(m => m.archived === true && notExpired(m) && (
            m.scope === 'shared' || m.toUid === profile.uid || m.fromUid === profile.uid
          ))
        : all.filter(m => !m.archived);
      setMessages(filtered);
      setLoading(false);
    }, err => { console.error('mailbox subscribe failed', err); setLoading(false); });
    return unsub;
  }, [profile?.uid, tab, refreshKey]);

  // Tab badges count UNREAD messages only (zero = no badge shown).
  // For Sent + Archived, counts will almost always be zero — showing
  // them only when there's something genuinely demanding attention
  // keeps the inbox quiet.
  const [counts, setCounts] = useState({ shared: 0, mine: 0, sent: 0, archived: 0 });
  useEffect(() => {
    if (!profile?.uid) return;
    const myUid = profile.uid;
    const isUnreadFor = (data, requireIncoming = true) => {
      if (data.archived) return false;
      if (requireIncoming && data.direction !== 'in') return false;
      return !(data.readBy || []).includes(myUid);
    };

    const subs = [];
    subs.push(onSnapshot(query(collection(db, 'mailbox'), where('scope', '==', 'shared')),
      s => setCounts(c => ({ ...c, shared: s.docs.filter(d => isUnreadFor(d.data())).length }))));
    subs.push(onSnapshot(query(collection(db, 'mailbox'), where('toUid', '==', profile.uid)),
      s => setCounts(c => ({ ...c, mine: s.docs.filter(d => isUnreadFor(d.data())).length }))));
    // Sent — typically already-read since you sent them. Only flagged
    // unread if you sent and haven't reopened (rare); shown at 0 → hidden.
    if (profile.role === 'admin') {
      subs.push(onSnapshot(query(collection(db, 'mailbox'), where('direction', '==', 'out')),
        s => setCounts(c => ({ ...c, sent: s.docs.filter(d => !d.data().archived && !(d.data().readBy || []).includes(myUid)).length }))));
    } else {
      subs.push(onSnapshot(query(collection(db, 'mailbox'), where('fromUid', '==', profile.uid)),
        s => setCounts(c => ({ ...c, sent: s.docs.filter(d => !d.data().archived && !(d.data().readBy || []).includes(myUid)).length }))));
    }
    // Archived: count any unread archived messages visible to me.
    subs.push(onSnapshot(query(collection(db, 'mailbox'), where('scope', '==', 'shared')),
      s => setCounts(c => ({ ...c, archived_shared: s.docs.filter(d => d.data().archived && !(d.data().readBy || []).includes(myUid)).length }))));
    subs.push(onSnapshot(query(collection(db, 'mailbox'), where('toUid', '==', profile.uid)),
      s => setCounts(c => ({ ...c, archived_mine: s.docs.filter(d => d.data().archived && !(d.data().readBy || []).includes(myUid)).length }))));
    subs.push(onSnapshot(query(collection(db, 'mailbox'), where('fromUid', '==', profile.uid)),
      s => setCounts(c => ({ ...c, archived_sent: s.docs.filter(d => d.data().archived && !(d.data().readBy || []).includes(myUid)).length }))));
    return () => subs.forEach(u => u());
  }, [profile?.uid, profile?.role]);

  const totalArchived = (counts.archived_shared || 0) + (counts.archived_mine || 0) + (counts.archived_sent || 0);

  const markRead = async (m) => {
    if (!m || (m.readBy || []).includes(profile.uid)) return;
    try {
      await updateDoc(doc(db, 'mailbox', m.id), {
        readBy: arrayUnion(profile.uid)
      });
    } catch (e) { /* may fail on rules; non-fatal */ }
  };

  const openMessage = (m) => {
    setOpen(m);
    markRead(m);
  };

  // Archive / restore. We never hard-delete — message stays in Firestore
  // with archived: true so it can be restored or audited later.
  const archive = async (m, restore = false) => {
    try {
      await updateDoc(doc(db, 'mailbox', m.id), {
        archived: !restore,
        archivedAt: restore ? null : new Date(),
        archivedBy: restore ? null : { uid: profile.uid, name: profile.name || profile.email }
      });
      if (open?.id === m.id) setOpen(null);
    } catch (e) {
      console.error('archive failed', e);
    } finally {
      setPendingArchive(null);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Inbox"
        title="Email"
        sub="Personal inboxes for T.Laketia.J@homeiqa.com and Denicia.W@homeiqa.com. Everything else (contracts@, hello@, support@…) lands in the shared inbox both agents can see."
        right={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {lastRefreshAt && (
              <span style={{ fontSize: 12, color: LIGHT, fontStyle: 'italic' }}>
                Refreshed {lastRefreshAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <Btn variant="ghost" onClick={manualRefresh} disabled={refreshing} title="Re-fetch the inbox now — useful when you're waiting on a verification email">
              {refreshing ? '⟳ Refreshing…' : '🔄 Refresh'}
            </Btn>
            <Btn variant="gold" onClick={() => { setReply(null); setShowCompose(true); }}>+ New email</Btn>
          </div>
        }
      />

      <div style={s.tabs}>
        <Tab label="Shared inbox"  count={counts.shared} active={tab === 'shared'}   onClick={() => { setTab('shared'); setOpen(null); }} />
        <Tab label="My inbox"      count={counts.mine}   active={tab === 'mine'}     onClick={() => { setTab('mine'); setOpen(null); }} />
        <Tab label="Sent"          count={counts.sent}   active={tab === 'sent'}     onClick={() => { setTab('sent'); setOpen(null); }} />
        <Tab label="Archived"      count={totalArchived} active={tab === 'archived'} onClick={() => { setTab('archived'); setOpen(null); }} muted />
      </div>

      <div style={isMobile ? s.splitMobile : s.split}>
        {/* Message list — hidden on mobile when a message is open
            (single-pane mobile UX). The Back button on the reading
            pane returns to the list. */}
        {(!isMobile || !open) && (
        <Card padded={false} style={isMobile ? s.listColMobile : s.listCol}>
          {loading ? (
            <div style={s.empty}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyTitle}>{emptyTitleFor(tab)}</div>
              <div style={s.emptyDesc}>{emptyDescFor(tab)}</div>
            </div>
          ) : (
            <ul style={s.list}>
              {messages.map(m => {
                const isUnread = !(m.readBy || []).includes(profile.uid);
                const isArchived = m.archived === true;
                return (
                  <li
                    key={m.id}
                    onClick={() => openMessage(m)}
                    style={{ ...s.row, ...(open?.id === m.id ? s.rowActive : {}), ...(isUnread && tab !== 'sent' && !isArchived ? s.rowUnread : {}) }}
                  >
                    {isUnread && tab !== 'sent' && !isArchived && <div style={s.unreadDot} />}
                    <div style={s.rowMain}>
                      <div style={s.rowFrom}>
                        {m.direction === 'in'
                          ? (m.from?.name || m.from?.email || 'Unknown')
                          : 'You → ' + (Array.isArray(m.to) ? m.to[0] : m.to)}
                        <span style={s.rowAddr}>· {m.recipient || (Array.isArray(m.to) ? m.to[0] : m.to) || ''}</span>
                      </div>
                      <div style={s.rowSubject}>
                        {m.kind === 'contract' && <Pill color="gold">📋 Contract</Pill>}
                        {m.kind === 'signed_contract' && <Pill color="green">🖊 Signed</Pill>}
                        {(m.attachments || []).length > 0 && <span style={s.attachIcon} title={`${m.attachments.length} attachment${m.attachments.length === 1 ? '' : 's'}`}>📎</span>}
                        {(m.kind === 'contract' || m.kind === 'signed_contract' || (m.attachments || []).length > 0) && ' '}
                        {m.subject || '(no subject)'}
                      </div>
                      <div style={s.rowSnippet}>{(m.bodyText || stripTags(m.bodyHtml) || '').slice(0, 110)}</div>
                    </div>
                    <div style={s.rowRight}>
                      <div style={s.rowTime}>{relativeAgo(m.sentAt?.toMillis?.())}</div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setPendingArchive({ msg: m, action: isArchived ? 'restore' : 'archive' }); }}
                        style={s.rowAction}
                        title={isArchived ? 'Restore from archive' : 'Move to archive'}
                      >
                        {isArchived ? '↩' : '🗑'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        )}

        {/* Reading pane — hidden on mobile when nothing's selected
            (the list takes the whole viewport instead). */}
        {(!isMobile || open) && (
        <Card padded={false} style={s.readCol}>
          {!open ? (
            <div style={{ ...s.empty, padding: '48px 24px' }}>
              <div style={s.emptyTitle}>Pick a message</div>
              <div style={s.emptyDesc}>Click any email on the left to read it here.</div>
            </div>
          ) : (
            <>
              <div style={s.readHead}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isMobile && (
                    <button onClick={() => setOpen(null)} style={s.backBtnMobile}>← Back to inbox</button>
                  )}
                  <div style={s.readSubject}>{open.subject}</div>
                  <div style={s.readMeta}>
                    {open.direction === 'in' ? 'From' : 'You sent to'}: <strong style={{ color: BURG_DK }}>{open.direction === 'in' ? (open.from?.email || open.from?.name) : (Array.isArray(open.to) ? open.to.join(', ') : open.to)}</strong>
                  </div>
                  <div style={s.readMeta}>
                    {open.direction === 'in' ? 'To' : 'From'}: <strong style={{ color: BURG_DK }}>{open.direction === 'in' ? open.recipient : (open.from?.email || '')}</strong>
                    {' · '}
                    {open.sentAt?.toDate?.()?.toLocaleString?.()}
                  </div>
                  {open.archived && (
                    <div style={{ ...s.readMeta, marginTop: 8, color: BURG }}>
                      📂 Archived{open.archivedAt?.toDate ? ` ${open.archivedAt.toDate().toLocaleString()}` : ''}{open.archivedBy?.name ? ` by ${open.archivedBy.name}` : ''}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {open.direction === 'in' && !open.archived && (
                    <Btn variant="gold" onClick={() => { setReply(open); setShowCompose(true); }}>Reply</Btn>
                  )}
                  {open.archived ? (
                    <Btn variant="ghost" onClick={() => setPendingArchive({ msg: open, action: 'restore' })}>↩ Restore</Btn>
                  ) : (
                    <Btn variant="danger" onClick={() => setPendingArchive({ msg: open, action: 'archive' })}>🗑 Archive</Btn>
                  )}
                </div>
              </div>
              <div style={s.readBody}>
                {(open.attachments || []).length > 0 && (
                  <div style={s.attachWrap}>
                    <div style={s.attachLabel}>Attachments</div>
                    <ul style={s.attachList}>
                      {open.attachments.map((a, i) => (
                        <li key={i} style={s.attachItem}>
                          <span style={s.attachFileIcon}>{a.type === 'pdf' ? '📄' : a.type === 'sign-link' ? '🔗' : '📎'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={s.attachName}>{a.name || 'Attachment'}</div>
                            {a.contentType && <div style={s.attachMeta}>{a.contentType}{a.size ? ` · ${formatBytes(a.size)}` : ''}</div>}
                          </div>
                          {a.url && (
                            <a href={a.url} target="_blank" rel="noreferrer" style={s.attachDownload}>
                              {a.type === 'sign-link' ? 'Open' : 'Download'} →
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {open.bodyHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizeBasic(open.bodyHtml) }} style={{ fontSize: 16, color: '#1C1C1C', lineHeight: 1.7 }} />
                ) : (
                  <pre style={s.readPre}>{open.bodyText}</pre>
                )}
              </div>
            </>
          )}
        </Card>
        )}
      </div>

      {pendingArchive && (
        <ConfirmArchiveModal
          msg={pendingArchive.msg}
          action={pendingArchive.action}
          onCancel={() => setPendingArchive(null)}
          onConfirm={() => archive(pendingArchive.msg, pendingArchive.action === 'restore')}
        />
      )}

      {showCompose && (
        <ComposeModal
          reply={reply}
          agent={profile}
          onClose={() => { setShowCompose(false); setReply(null); }}
          onSent={() => { setShowCompose(false); setReply(null); }}
        />
      )}
    </>
  );
}

function ConfirmArchiveModal({ msg, action, onCancel, onConfirm }) {
  const isRestore = action === 'restore';
  return (
    <div style={cm.overlay} onClick={onCancel}>
      <div style={{ ...cm.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div style={cm.head}>
          <div>
            <div style={cm.eyebrow}>{isRestore ? 'Restore message' : 'Archive message'}</div>
            <h2 style={cm.title}>{isRestore ? 'Bring this back?' : 'Move to archive?'}</h2>
          </div>
          <button onClick={onCancel} style={cm.x}>×</button>
        </div>
        <div style={{ padding: '4px 24px 22px', fontSize: 14, color: MID, lineHeight: 1.65 }}>
          <div style={{ marginBottom: 14 }}>
            <strong style={{ color: BURG_DK }}>{msg.subject || '(no subject)'}</strong>
          </div>
          <p style={{ margin: 0 }}>
            {isRestore
              ? 'This message will return to its original tab. You can find it under Shared, My inbox, or Sent.'
              : 'The message stays saved in your archive. You can always restore it from the Archived tab.'}
          </p>
          <div style={cm.actions}>
            <Btn type="button" variant="ghost" onClick={onCancel}>Cancel</Btn>
            <Btn type="button" variant={isRestore ? 'gold' : 'danger'} onClick={onConfirm}>
              {isRestore ? '↩ Restore' : '🗑 Archive'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComposeModal({ reply, agent, onClose, onSent }) {
  const [to, setTo] = useState(reply?.from?.email || '');
  const [subject, setSubject] = useState(reply ? `Re: ${reply.subject?.replace(/^re:\s*/i, '') || ''}` : '');
  const [body, setBody] = useState(reply ? `\n\n\n———\nOn ${reply.sentAt?.toDate?.().toLocaleString()}, ${reply.from?.email || ''} wrote:\n${(reply.bodyText || '').split('\n').map(l => '> ' + l).join('\n')}` : '');
  const [scope, setScope] = useState(reply?.scope || 'shared');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!to.trim()) { setErr('Recipient email required.'); return; }
    if (!subject.trim()) { setErr('Subject required.'); return; }
    if (!body.trim()) { setErr('Message body required.'); return; }
    setSending(true);
    try {
      const fn = httpsCallable(functions, 'sendMailboxEmail');
      await fn({
        to: to.trim(),
        subject: subject.trim(),
        bodyText: body,
        bodyHtml: `<p>${body.replace(/\n/g, '<br/>')}</p>`,
        scope,
        threadKey: reply?.threadKey || null,
        inReplyTo: reply?.messageId || null
      });
      onSent();
    } catch (e) {
      setErr(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={cm.overlay} onClick={onClose}>
      <div style={cm.modal} onClick={e => e.stopPropagation()}>
        <div style={cm.head}>
          <div>
            <div style={cm.eyebrow}>{reply ? 'Reply' : 'New message'}</div>
            <h2 style={cm.title}>{reply ? 'Reply to ' + (reply.from?.email || '') : 'Compose email'}</h2>
          </div>
          <button onClick={onClose} style={cm.x}>×</button>
        </div>
        <div style={{ padding: '4px 24px 22px' }}>
          <Field label="To" required><Input value={to} onChange={e => setTo(e.target.value)} placeholder="someone@example.com" type="email" /></Field>
          <Field label="Subject" required><Input value={subject} onChange={e => setSubject(e.target.value)} /></Field>
          <Field label="Save to mailbox" hint="Personal = only you see it. Shared = both agents see it.">
            <select value={scope} onChange={e => setScope(e.target.value)} style={{ padding: '10px 12px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 14, fontFamily: "'DM Sans', sans-serif", background: '#fff' }}>
              <option value="shared">Shared inbox (both agents)</option>
              <option value="personal">Personal inbox (only me)</option>
            </select>
          </Field>
          <Field label="Message"><TextArea rows={10} value={body} onChange={e => setBody(e.target.value)} /></Field>
          {err && <div style={cm.err}>{err}</div>}
          <div style={cm.actions}>
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn type="button" variant="gold" onClick={submit} disabled={sending}>
              {sending ? 'Sending…' : '📤 Send email'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// Count badge only renders when there's an unread message demanding
// attention. Zero = hidden, keeps the tab strip quiet when nothing
// new has arrived.
const Tab = ({ label, count, active, onClick, muted }) => (
  <button onClick={onClick} style={{ ...s.tab, ...(active ? s.tabActive : {}), ...(muted && !active ? s.tabMuted : {}) }}>
    {label}
    {count > 0 && (
      <span style={{ ...s.tabCount, ...(active ? s.tabCountActive : {}) }}>{count}</span>
    )}
  </button>
);

function relativeAgo(ms) {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  return `${w}w`;
}

function formatBytes(b) {
  if (!b || b < 1024) return `${b || 0} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function stripTags(s) {
  return String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Very basic HTML sanitization — drops <script>, <iframe>, on* attributes.
function sanitizeBasic(html) {
  return String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '');
}

function emptyTitleFor(tab) {
  if (tab === 'shared')   return 'Shared inbox is empty';
  if (tab === 'mine')     return 'Your personal inbox is empty';
  if (tab === 'archived') return 'No archived messages';
  return 'No sent messages';
}
function emptyDescFor(tab) {
  if (tab === 'shared')   return 'Replies to contracts@homeiqa.com, hello@homeiqa.com, etc. arrive here. Both agents see this view.';
  if (tab === 'mine')     return 'Replies sent specifically to your address arrive here. Only you can see this view.';
  if (tab === 'archived') return 'Anything you archive shows up here. Open and click Restore to bring it back.';
  return 'Click "+ New email" to send your first message.';
}

const s = {
  // Tabs — significantly larger so they're easy to scan from across the room.
  tabs: { display: 'flex', gap: 8, marginBottom: 18, borderBottom: `1px solid ${BORDER}`, flexWrap: 'wrap' },
  tab: { background: 'transparent', border: 'none', padding: '14px 22px', fontSize: 18, color: MID, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: 10, position: 'relative', top: 1, fontWeight: 500 },
  tabActive: { color: BURG_DK, borderBottom: `3px solid ${GOLD}`, fontWeight: 600 },
  tabMuted: { color: LIGHT },
  tabCount: { fontSize: 14, padding: '3px 12px', borderRadius: 999, background: '#F4F4F5', color: LIGHT, fontWeight: 600 },
  tabCountActive: { background: GOLD_PALE, color: BURG },

  split: { display: 'grid', gridTemplateColumns: '420px 1fr', gap: 18, alignItems: 'flex-start' },
  listCol: { maxHeight: 'calc(100vh - 280px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  listColMobile: { maxHeight: 'none', display: 'flex', flexDirection: 'column' },
  splitMobile: { display: 'block' },
  backBtnMobile: { background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '8px 14px', fontSize: 13, color: BURG, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, marginBottom: 12 },
  list: { listStyle: 'none', padding: 0, margin: 0, overflowY: 'auto', flex: 1 },

  // List rows — bumped sender name/subject up to 16-18px from 12-13px.
  row: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 18px', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer' },
  rowActive: { background: CREAM },
  rowUnread: { background: 'rgba(201,168,76,0.06)' },
  unreadDot: { width: 9, height: 9, borderRadius: '50%', background: BURG, marginTop: 7, flexShrink: 0 },
  rowMain: { flex: 1, minWidth: 0 },
  rowFrom: { fontSize: 16, color: BURG_DK, fontWeight: 600, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowAddr: { color: LIGHT, fontWeight: 400, marginLeft: 6, fontSize: 14 },
  rowSubject: { fontSize: 16, color: '#1C1C1C', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowSnippet: { fontSize: 13, color: LIGHT, lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 },
  rowTime: { fontSize: 13, color: LIGHT, whiteSpace: 'nowrap', marginTop: 2 },
  rowAction: { background: 'transparent', border: '1px solid transparent', borderRadius: 6, width: 30, height: 30, fontSize: 16, color: LIGHT, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },

  // Reading pane — body bumped from 14 to 16px, meta from 11 to 14.
  readCol: { minHeight: 400 },
  readHead: { padding: '24px 28px 18px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  readSubject: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: BURG_DK, marginBottom: 12, lineHeight: 1.2 },
  readMeta: { fontSize: 14, color: LIGHT, lineHeight: 1.7 },
  readBody: { padding: '24px 28px', maxHeight: 'calc(100vh - 380px)', overflowY: 'auto' },
  readPre: { fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: '#1C1C1C', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 },

  attachIcon: { display: 'inline-block', marginRight: 6, fontSize: 14, color: GOLD },
  attachWrap: { background: GOLD_PALE, border: '1px solid rgba(201,168,76,0.4)', borderRadius: 10, padding: '14px 16px', marginBottom: 18 },
  attachLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: BURG, marginBottom: 10 },
  attachList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  attachItem: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 14px' },
  attachFileIcon: { fontSize: 22, lineHeight: 1, flexShrink: 0 },
  attachName: { fontSize: 14, color: BURG_DK, fontWeight: 500 },
  attachMeta: { fontSize: 11, color: LIGHT, marginTop: 2 },
  attachDownload: { fontSize: 12, fontWeight: 500, color: BURG, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${BORDER}`, borderRadius: 999, whiteSpace: 'nowrap' },

  empty: { padding: '40px 24px', textAlign: 'center' },
  emptyTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: BURG_DK, marginBottom: 10 },
  emptyDesc: { fontSize: 14, color: LIGHT, lineHeight: 1.6, maxWidth: 360, margin: '0 auto' }
};

const cm = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px', overflowY: 'auto' },
  modal: { background: '#fff', borderRadius: 8, maxWidth: 640, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  head: { padding: '20px 24px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  eyebrow: { fontSize: 11, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 500, color: BURG_DK, margin: 0, letterSpacing: '-0.3px' },
  x: { background: 'transparent', border: 'none', fontSize: 28, color: LIGHT, cursor: 'pointer', lineHeight: 1, padding: 0 },
  err: { padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 13, marginTop: 10 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BORDER}` }
};
